import { Injectable, signal, effect, inject } from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { LoggingService } from '../shared/logging/logging.service';

@Injectable({
  providedIn: 'root',
})
export class AdminSessionService {
  private readonly SESSION_KEY = 'admin-session-id';
  
  // Signal to track the current admin identifier
  private _adminIdentifier = signal<string>('');
  public readonly adminIdentifier = this._adminIdentifier.asReadonly();

  private authService = inject(AuthService);
  private loggingService = inject(LoggingService);

  constructor() {
    // Effect to monitor authentication state changes
    effect(() => {
      const isAuthenticated = this.authService.isAuthenticated();
      
      if (isAuthenticated) {
        this.initializeAdminSession();
      } else {
        this.clearAdminSession();
      }
    });
  }

  /**
   * Generates a new admin identifier with format 'admin-{guid}'
   */
  private generateAdminIdentifier(): string {
    const guid = this.generateGuid();
    return `admin-${guid}`;
  }

  /**
   * Generates a RFC4122 version 4 GUID
   */
  private generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Initializes the admin session with a unique identifier
   */
  private initializeAdminSession(): void {
    // Check if we already have a session ID for this login session
    let adminId = sessionStorage.getItem(this.SESSION_KEY);
    
    if (!adminId) {
      // Generate new admin identifier for this session
      adminId = this.generateAdminIdentifier();
      sessionStorage.setItem(this.SESSION_KEY, adminId);
      this.loggingService.debug('AdminSessionService', `Generated new admin identifier: ${adminId}`);
    } else {
      this.loggingService.debug('AdminSessionService', `Using existing admin identifier: ${adminId}`);
    }

    this._adminIdentifier.set(adminId);
  }

  /**
   * Clears the admin session
   */
  private clearAdminSession(): void {
    sessionStorage.removeItem(this.SESSION_KEY);
    this._adminIdentifier.set('');
    this.loggingService.debug('AdminSessionService', 'Admin session cleared');
  }

  /**
   * Gets the current admin identifier
   * @returns The admin identifier (e.g., 'admin-12345678-1234-4567-8901-123456789012') or empty string if not authenticated
   */
  getAdminIdentifier(): string {
    return this._adminIdentifier();
  }

  /**
   * Forces regeneration of the admin identifier (useful for testing or manual refresh)
   */
  regenerateAdminIdentifier(): void {
    if (this.authService.isAuthenticated()) {
      sessionStorage.removeItem(this.SESSION_KEY);
      this.initializeAdminSession();
      this.loggingService.debug('AdminSessionService', 'Admin identifier regenerated');
    }
  }
}